import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Popper,
  Icons,
  iconSize,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { AccessibleScope } from '@munifocus/contracts-gov';
import { useOptionalScope } from '../../providers/ScopeProvider';

/**
 * Remembers that the "what is this" bubble has been read, per browser.
 *
 * Deliberately not per account: the thing being explained is a concept, not a
 * fact about one login, and somebody who has understood it once does not need
 * teaching again because they switched accounts. A stale key costs nothing —
 * the explanation stays reachable from the info button forever.
 */
const SCOPE_INTRO_KEY = 'gov.scopeIntroSeen';

/** Ties the info button to the bubble it discloses. */
const INTRO_ID = 'gov-scope-intro';

function introSeen(): boolean {
  try {
    return localStorage.getItem(SCOPE_INTRO_KEY) === '1';
  } catch {
    // Private window or blocked site data. Treat as seen rather than showing an
    // unclosable bubble on every page load.
    return true;
  }
}

function rememberIntroSeen(): void {
  try {
    localStorage.setItem(SCOPE_INTRO_KEY, '1');
  } catch {
    // Nothing to remember it with; the bubble simply shows again next time.
  }
}

/**
 * Which municipality or company the officer is currently working in.
 *
 * Renders nothing for the common case — one seat, nothing to switch between —
 * so the chrome only grows for the people who need it: a shared regional
 * officer, a clerk at two villages, a contractor's administrator.
 *
 * Switching re-scopes the whole portal, not just this screen. That is the point:
 * an officer appointed at two municipalities looking at "the file list" has to
 * be looking at one municipality's list, or the queue is two towns interleaved.
 *
 * **What it had to start saying.** The data was always here; almost none of it
 * reached the screen. Three things were missing and each produced its own
 * confusion:
 *
 * 1. The button led with the *kind* — "Local government" above "Moncton" —
 *    which the name already tells you. What an officer needs is what they *are*
 *    there, because that is what decides what the rest of the portal will let
 *    them do. The role leads now.
 * 2. Municipalities and companies were one flat list. They are not the same
 *    kind of thing: a company is who you work for, a municipality is where you
 *    work. They are grouped under headings.
 * 3. A contractor's municipalities are generated from their employer's
 *    contracts, so the list offered places the person has no direct
 *    relationship with, beside the company that is the reason they are there,
 *    with nothing connecting the two. {@link AccessibleScope.via} carries that
 *    attribution and it is printed under the name.
 */
export function ScopeSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation();
  const scope = useOptionalScope();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [introOpen, setIntroOpen] = useState(false);
  const introAnchor = useRef<HTMLButtonElement | null>(null);
  const needsSwitcher = Boolean(scope?.needsSwitcher);

  // Shown once, the first time somebody has somewhere to switch between.
  //
  // Gated on `needsSwitcher` rather than on mount: a person with one seat never
  // sees the control, and explaining a control that is not there is worse than
  // saying nothing. The flag is written when it opens, not when it closes, so a
  // reload mid-read does not queue it up again.
  //
  // Also gated on the rail being expanded, and that is not cosmetic: collapsed
  // renders no info button, so there is nothing to anchor the bubble to and
  // nothing to reopen it with. Firing here anyway would mark the introduction
  // as seen while showing nobody anything — and the collapsed state is
  // remembered across visits, so that account would never meet it again. It
  // waits instead, and opens the first time the rail is expanded.
  useEffect(() => {
    if (collapsed || !needsSwitcher || introSeen()) return;
    setIntroOpen(true);
    rememberIntroSeen();
  }, [collapsed, needsSwitcher]);

  // Collapsing while it is open would strand the bubble on a detached anchor.
  useEffect(() => {
    if (collapsed) setIntroOpen(false);
  }, [collapsed]);

  // Nothing to switch between: one seat, or a shell rendered outside the
  // provider (an isolated component test). Both are states, not errors.
  if (!scope?.needsSwitcher || !scope.active) return null;
  const { scopes, active, setActive } = scope;

  const roleLabel = (entry: AccessibleScope) =>
    entry.roles.map((role) => t(`gov.admin.roles.${role}`)).join(', ');

  const governments = scopes.filter((entry) => entry.kind === 'local_government');
  const companies = scopes.filter((entry) => entry.kind === 'company');

  const renderGroup = (heading: string, entries: AccessibleScope[], showDivider: boolean) => {
    if (!entries.length) return null;
    return [
      showDivider ? <Divider key={`${heading}-divider`} sx={{ my: 0.5 }} /> : null,
      // `disableSticky` because the menu is short and a pinned heading over
      // three rows reads as a broken list rather than a section.
      <ListSubheader key={heading} disableSticky sx={{ lineHeight: 2.2, fontSize: 11.5 }}>
        {heading}
      </ListSubheader>,
      ...entries.map((entry) => {
        const isActive = entry.kind === active.kind && entry.id === active.id;
        return (
          <MenuItem
            key={`${entry.kind}:${entry.id}`}
            selected={isActive}
            // A single-choice chooser, not a list of commands: `menuitemradio`
            // is what tells a screen reader that one of these is currently
            // chosen and picking another replaces it. A plain `menuitem` leaves
            // the check mark meaningful only to people who can see it.
            role="menuitemradio"
            aria-checked={isActive}
            onClick={() => {
              setActive(entry);
              setAnchorEl(null);
            }}
            sx={{ alignItems: 'flex-start', gap: 1 }}
          >
            {/* A reserved column either way, so names line up whether or not
                the row is the active one. */}
            <Box sx={{ width: iconSize.md, flexShrink: 0, pt: 0.4 }}>
              {isActive && <Icons.Check size={iconSize.md} />}
            </Box>
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2">{entry.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {roleLabel(entry)}
              </Typography>
              {entry.via && (
                <Typography variant="caption" color="text.secondary">
                  {t('gov.scope.via', { company: entry.via.name })}
                </Typography>
              )}
            </Stack>
          </MenuItem>
        );
      }),
    ];
  };

  return (
    <Box sx={{ px: collapsed ? 0.5 : 1.5, pb: 1 }}>
      {!collapsed && (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', pl: 1 }}
        >
          <Typography variant="caption" sx={{ opacity: 0.7, lineHeight: 1.6 }}>
            {t('gov.scope.label')}
          </Typography>
          {/* Stays after the introduction is dismissed. A one-time bubble that
              leaves nothing behind is unhelpful to whoever closed it without
              reading, and to anybody who meets the control months later. */}
          <IconButton
            ref={introAnchor}
            size="small"
            aria-label={t('gov.scope.whatIsThis')}
            // A disclosure, so its state and target are announced rather than
            // left to whoever can see the bubble appear.
            aria-expanded={introOpen}
            aria-controls={introOpen ? INTRO_ID : undefined}
            onClick={() => setIntroOpen((open) => !open)}
            sx={{ color: 'inherit', opacity: 0.7 }}
          >
            <Icons.Info size={iconSize.sm} />
          </IconButton>
        </Stack>
      )}
      <Button
        fullWidth
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-label={t('gov.scope.switchAria', { scope: active.name })}
        sx={{
          justifyContent: collapsed ? 'center' : 'space-between',
          textTransform: 'none',
          color: 'inherit',
          minWidth: 0,
        }}
        endIcon={collapsed ? undefined : <Icons.ChevronDown size={iconSize.md} />}
      >
        {collapsed ? (
          <Icons.Landmark size={iconSize.lg} />
        ) : (
          <Stack sx={{ alignItems: 'flex-start', minWidth: 0 }}>
            {/* The role, not the kind: "Clerk" over "Moncton" tells an officer
                what this place lets them do; "Local government" over "Moncton"
                tells them what they can already read. */}
            <Typography variant="caption" sx={{ opacity: 0.7, lineHeight: 1.2 }}>
              {roleLabel(active)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {active.name}
            </Typography>
          </Stack>
        )}
      </Button>

      {/* Non-modal on purpose — see the `Popper` note in libs/ui. Being told
          what a control does must not lock the portal behind the telling. */}
      <Popper
        open={introOpen}
        anchorEl={introAnchor.current}
        placement="bottom-start"
        id={INTRO_ID}
        role="note"
        aria-label={t('gov.scope.intro.title')}
        // Above the page, below any dialog. At tooltip height it sat on top of
        // whatever dialog the person opened before dismissing it — a Decline
        // dialog half-covered by "You work in more than one place".
        sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
      >
        <Paper elevation={8} sx={{ maxWidth: 320, p: 2, m: 1 }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2">{t('gov.scope.intro.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('gov.scope.intro.body')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('gov.scope.intro.effect')}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Button size="small" variant="contained" onClick={() => setIntroOpen(false)}>
                {t('gov.scope.intro.dismiss')}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Popper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 260, maxWidth: 340 } } }}
      >
        {renderGroup(t('gov.scope.groups.municipalities'), governments, false)}
        {renderGroup(t('gov.scope.groups.companies'), companies, governments.length > 0)}
        <Divider sx={{ my: 0.5 }} />
        {/* Said once, at the bottom, because the consequence of switching is not
            obvious from a list of names: the file list, dashboard and map all
            follow it.
            
            Wrapped in a presentational row because the menu's children are its
            items: bare prose under `ul[role="menu"]` is content a screen reader
            may skip or announce as a command. `role="presentation"` keeps it on
            screen and out of the item count. */}
        <Box
          component="li"
          role="presentation"
          sx={{ px: 2, py: 0.5, maxWidth: 320, whiteSpace: 'normal' }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('gov.scope.hint')}
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
}
